import { useAbortableConfirm } from "../lib/useAbortableConfirm";
import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import FieldHint from "../components/FieldHint";
import { api, ApiError, isSupportUser, SUPPORT_ROLE_LABEL, type AdminPaymentAccountResponse, type PlatformFeeReadonly, type PlatformPaymentSettings } from "../lib/api";
import BankAccountsSection from "./BankAccountsSection";
import "./CuentasCobro.css";

function mpStatusLabel(status: string): string {
  switch (status) {
    case "active":
      return "Conectada";
    case "disabled":
      return "Necesita reconexión";
    case "not_connected":
      return "No conectada";
    default:
      return status;
  }
}

const FEE_TYPE_LABELS: Record<string, string> = {
  fixed: "Monto fijo por inscripción",
  percent: "Porcentaje",
  fixed_plus_percent: "Monto fijo + porcentaje",
};

/** «99.00» → «$99»; «12.50» → «$12.50». */
export function formatMoney(value: string | number | null | undefined): string {
  const amount = Number(value ?? 0) || 0;
  return `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
}

/** El sistema guarda puntos base (100 = 1 %); en pantalla se captura en %. */
export function bpsToPercent(bps: number | null | undefined): string {
  const value = (Number(bps ?? 0) || 0) / 100;
  return String(Number(value.toFixed(2)));
}

export function percentToBps(percent: string): number {
  const value = Number(String(percent).replace(",", ".").replace("%", "").trim());
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.round(value * 100);
}

function feeUsesPercent(type: string): boolean {
  return type === "percent" || type === "fixed_plus_percent";
}

function feeUsesFixed(type: string): boolean {
  return type === "fixed" || type === "fixed_plus_percent";
}

/** «Comisión $99 por inscripción», «Comisión 5 %», «Comisión $25 + 5 %». */
export function feeSummary(fee: PlatformFeeReadonly | null | undefined): string | null {
  if (!fee) return null;
  if (!fee.is_active) return "Sin comisión activa";
  const fixed = formatMoney(fee.platform_fee_fixed_amount);
  const percent = `${bpsToPercent(fee.platform_fee_percent_bps)} %`;
  if (fee.platform_fee_type === "percent") return `Comisión ${percent} por inscripción`;
  if (fee.platform_fee_type === "fixed_plus_percent") return `Comisión ${fixed} + ${percent} por inscripción`;
  return `Comisión ${fixed} por inscripción`;
}

type PlatformForm = {
  currency_default: string;
  platform_fee_type: string;
  platform_fee_fixed_amount: string;
  platform_fee_percent: string;
  minimum_platform_fee_amount: string;
  is_active: boolean;
};

function formFromSettings(next: PlatformPaymentSettings): PlatformForm {
  return {
    currency_default: next.currency_default || "MXN",
    platform_fee_type: next.platform_fee_type || "fixed",
    platform_fee_fixed_amount: String(Number(next.platform_fee_fixed_amount || 0)),
    platform_fee_percent: bpsToPercent(next.platform_fee_percent_bps),
    minimum_platform_fee_amount: String(Number(next.minimum_platform_fee_amount || 0)),
    is_active: next.is_active,
  };
}

function modeChip(mode: string | null | undefined, long = false) {
  return mode === "live" ? (
    <span className="cobro-chip cobro-chip--ok">Cobro real</span>
  ) : (
    <span className="cobro-chip cobro-chip--test">{long ? "Modo prueba · sin dinero real" : "Modo prueba"}</span>
  );
}

export default function AdminPaymentsPage() {
  const { token, hasRole, user } = useAuth();
  const { askConfirm } = useAbortableConfirm([user?.id]);
  const isSuperadmin = hasRole("superadmin");
  const isSupport = isSupportUser(user);
  const [payload, setPayload] = useState<AdminPaymentAccountResponse["data"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [platformSettings, setPlatformSettings] = useState<PlatformPaymentSettings | null>(null);
  const [mpActionLoading, setMpActionLoading] = useState(false);
  const mpReturnHandled = useRef(false);
  const [platformForm, setPlatformForm] = useState<PlatformForm>({
    currency_default: "MXN",
    platform_fee_type: "fixed",
    platform_fee_fixed_amount: "0",
    platform_fee_percent: "0",
    minimum_platform_fee_amount: "0",
    is_active: true,
  });

  const loadAccount = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [response, settingsResponse] = await Promise.all([
        api.payments.account(token),
        isSuperadmin ? api.payments.platformSettings(token) : Promise.resolve(null),
      ]);
      setPayload(response.data);
      if (settingsResponse) {
        const next = settingsResponse.data.settings;
        setPlatformSettings(next);
        setPlatformForm(formFromSettings(next));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo cargar la cuenta de cobro.");
    } finally {
      setLoading(false);
    }
  }, [token, isSuperadmin]);

  useEffect(() => {
    void loadAccount();
  }, [loadAccount]);

  // Regreso del OAuth de Mercado Pago: /admin/payments/mp-return?code=...&state=...
  // (o, por compatibilidad con el formato previo, /admin/payments?mp=return&code=...&state=...)
  useEffect(() => {
    if (mpReturnHandled.current) return;
    if (!token) return;
    const params = new URLSearchParams(window.location.search);
    const isMpReturn = window.location.pathname.endsWith("/mp-return") || params.get("mp") === "return";
    if (!isMpReturn) return;
    const code = params.get("code");
    const state = params.get("state");
    if (!code || !state) return;

    mpReturnHandled.current = true;
    const basePath = window.location.pathname.endsWith("/mp-return")
      ? window.location.pathname.slice(0, -"/mp-return".length)
      : window.location.pathname;
    window.history.replaceState(null, "", basePath || "/admin/payments");

    (async () => {
      setMpActionLoading(true);
      setError(null);
      try {
        await api.payments.mpCallback(token, { code, state });
        await loadAccount();
        setNotice("Mercado Pago conectado correctamente.");
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "No se pudo completar la conexión con Mercado Pago.");
      } finally {
        setMpActionLoading(false);
      }
    })();
  }, [token, loadAccount]);

  async function handleMpConnect() {
    if (!token) return;
    setMpActionLoading(true);
    setError(null);
    setNotice(null);
    try {
      const response = await api.payments.mpConnect(token);
      window.location.href = response.data.authorization_url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo iniciar la conexión con Mercado Pago.");
    } finally {
      setMpActionLoading(false);
    }
  }

  async function handleMpDisconnect() {
    if (!token) return;
    if (!(await askConfirm("¿Desconectar Mercado Pago? Los pagos en línea de tus torneos se pausarán.", { acceptLabel: "Sí, desconectar Mercado Pago", tone: "danger" }))) return;
    setMpActionLoading(true);
    setError(null);
    setNotice(null);
    try {
      const response = await api.payments.mpDisconnect(token);
      setPayload((current) =>
        current ? { ...current, mercadopago: { configured: true, account: response.data.account } } : current,
      );
      setNotice("Mercado Pago desconectado.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo desconectar Mercado Pago.");
    } finally {
      setMpActionLoading(false);
    }
  }

  async function handleSavePlatformSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token) return;
    setActionLoading(true);
    setError(null);
    setNotice(null);
    try {
      const response = await api.payments.updatePlatformSettings(token, {
        currency_default: platformForm.currency_default || "MXN",
        platform_fee_type: platformForm.platform_fee_type,
        platform_fee_fixed_amount: platformForm.platform_fee_fixed_amount || "0",
        platform_fee_percent_bps: percentToBps(platformForm.platform_fee_percent),
        minimum_platform_fee_amount: platformForm.minimum_platform_fee_amount || "0",
        is_active: platformForm.is_active,
      });
      const next = response.data.settings;
      setPlatformSettings(next);
      setPlatformForm(formFromSettings(next));
      setPayload((current) => (current ? { ...current, platform_fee: next } : current));
      setNotice("Comisión de Torneos123 guardada.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo guardar la comisión de Torneos123.");
    } finally {
      setActionLoading(false);
    }
  }

  // "Cambiar cuenta": mismo OAuth que conectar; el backend reemplaza la cuenta
  // ligada con el collector que autorice el organizador (store_oauth_tokens).
  async function handleMpChangeAccount() {
    // El backend revoca la autorización actual en Mercado Pago antes de abrir
    // el OAuth nuevo: así MP vuelve a mostrar su pantalla de autorización (con
    // opción de entrar con otra cuenta) en vez de aprobar en silencio la cuenta
    // con sesión en el navegador (Eduardo, 2026-09-04).
    if (
      !(await askConfirm(
        "¿Cambiar la cuenta de Mercado Pago?\n\nSe desconecta la cuenta actual y te llevamos a Mercado Pago para autorizar otra. Si el navegador ya tiene sesión de Mercado Pago, en esa pantalla elige \"Ingresar con otra cuenta\".\n\nLos pagos ya cobrados no se mueven.", { acceptLabel: "Sí, cambiar de cuenta" }))
    ) {
      return;
    }
    if (!token) return;
    setMpActionLoading(true);
    setError(null);
    setNotice(null);
    try {
      const response = await api.payments.mpChangeAccount(token);
      window.location.href = response.data.authorization_url;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "No se pudo iniciar el cambio de cuenta de Mercado Pago.");
      await loadAccount();
    } finally {
      setMpActionLoading(false);
    }
  }

  const mpConfigured = Boolean(payload?.mercadopago?.configured);
  const mpAccount = payload?.mercadopago?.account;
  const mpStatus = mpAccount?.account_status ?? "not_connected";
  const mpReady = mpConfigured && mpStatus === "active";
  const mode = platformSettings?.mode ?? payload?.platform_fee?.mode ?? payload?.mode ?? "test";
  const bankCount = payload?.bank_accounts?.length ?? 0;
  const fee: PlatformFeeReadonly | null = platformSettings ?? payload?.platform_fee ?? null;
  const summary = feeSummary(fee);
  const formUsesPercent = feeUsesPercent(platformForm.platform_fee_type);
  const formUsesFixed = feeUsesFixed(platformForm.platform_fee_type);

  if (isSupport) {
    // Soporte Torneos123 no conecta, cambia ni desconecta cuentas de cobro
    // (el servidor también lo niega).
    return (
      <div className="cobro-page">
        <section className="cobro-card cobro-page__head">
          <div className="cobro-page__title">
            <Link className="cobro-page__back" to="/admin/finance">← Finanzas</Link>
            <h1>Cuentas de cobro</h1>
          </div>
          <p className="support-money-note" role="note">
            Como {SUPPORT_ROLE_LABEL} no administras cuentas de cobro (Mercado Pago ni cuentas bancarias). Cada
            organizador conecta las suyas.
          </p>
        </section>
      </div>
    );
  }

  return (
    <div className="cobro-page">
      <section className="cobro-card cobro-page__head">
        <div className="cobro-page__title-row">
          <div className="cobro-page__title">
            <Link className="cobro-page__back" to="/admin/finance">← Finanzas</Link>
            <h1>Cuentas de cobro</h1>
            <p className="cobro-muted">Conecta Mercado Pago o agrega cuentas bancarias. En cada torneo eliges a cuál llegan los pagos.</p>
          </div>
          {isSuperadmin ? <span className="cobro-chip cobro-chip--sa">Viendo como superadmin</span> : null}
        </div>
        {payload ? (
          <div className="cobro-page__sums">
            {mpConfigured ? (
              <span className="cobro-sum">
                {mpReady ? <b aria-hidden="true">✓</b> : null}
                {mpReady ? "Mercado Pago conectado" : `Mercado Pago: ${mpStatusLabel(mpStatus).toLowerCase()}`}
              </span>
            ) : null}
            <span className="cobro-sum">{bankCount === 1 ? "1 cuenta bancaria" : `${bankCount} cuentas bancarias`}</span>
            {summary ? <span className="cobro-sum">{summary}</span> : null}
          </div>
        ) : null}
      </section>

      {error && <p className="form-error" role="alert">{error}</p>}
      {notice && <p className="alert alert--info">{notice}</p>}
      {loading ? <p className="muted">Cargando cuenta de cobro…</p> : null}

      {mpConfigured ? (
        <section className="cobro-card" aria-labelledby="cobro-mp-title">
          <div className="cobro-hd">
            <span className="cobro-num">1</span>
            <h2 id="cobro-mp-title">Mercado Pago · pagos en línea</h2>
            <FieldHint label="Cómo funciona el cobro con Mercado Pago">
              El jugador paga desde el link de inscripción, Torneos123 marca su inscripción como pagada en automático y todo queda en Finanzas. El dinero entra a la cuenta de Mercado Pago que conectes aquí.
            </FieldHint>
          </div>
          <p className="cobro-muted">El jugador paga desde el link de inscripción y su lugar se marca pagado solo.</p>

          <div className="cobro-cuenta cobro-mp">
            <div className="cobro-cuenta__main">
              <span className="cobro-cuenta__chips">
                <span className={`cobro-chip ${mpReady ? "cobro-chip--ok" : mpStatus === "disabled" ? "cobro-chip--warn" : "cobro-chip--muted"}`}>
                  {mpStatusLabel(mpStatus)}
                </span>
                {modeChip(mode, true)}
              </span>
              <strong>{mpAccount?.business_name || "Cuenta de Mercado Pago"}</strong>
              {mpReady && mpAccount?.provider_account_id ? <span>Cuenta conectada {mpAccount.provider_account_id}</span> : null}
              <span className="hint-label">
                Costo de Mercado Pago: 3.49% + $4 por cobro, más IVA
                <FieldHint label="Costo de cobrar con Mercado Pago">
                  Mercado Pago cobra al organizador 3.49% + $4.00 por cobro, más IVA, con el dinero disponible al instante (menos porcentaje si eliges liberación a 14 o 30 días en tu cuenta de Mercado Pago). Ejemplo: inscripción de $850 → Mercado Pago retiene aprox. $39. La comisión de Torneos123 se descuenta aparte y la ves en Finanzas.
                </FieldHint>
              </span>
            </div>
            {mpStatus === "not_connected" ? (
              <div className="cobro-cuenta__actions">
                <button className="btn btn--primary cobro-btn-lime" type="button" onClick={handleMpConnect} disabled={mpActionLoading}>
                  {mpActionLoading ? "Conectando…" : "Conectar Mercado Pago"}
                </button>
              </div>
            ) : mpStatus === "disabled" ? (
              <div className="cobro-cuenta__actions">
                <button className="btn btn--primary cobro-btn-lime" type="button" onClick={handleMpConnect} disabled={mpActionLoading}>
                  {mpActionLoading ? "Conectando…" : "Reconectar Mercado Pago"}
                </button>
              </div>
            ) : (
              <div className="cobro-cuenta__actions">
                <span className="registration-action-hint">
                  <button className="btn btn--ghost btn--small" type="button" onClick={handleMpChangeAccount} disabled={mpActionLoading}>
                    {mpActionLoading ? "Procesando…" : "Cambiar cuenta"}
                  </button>
                  <FieldHint label="Qué hace Cambiar cuenta">
                    Desconecta la cuenta actual, revoca su autorización en Mercado Pago y te lleva a autorizar otra. En la pantalla de Mercado Pago elige &quot;Ingresar con otra cuenta&quot; si el navegador ya tiene sesión. Los pagos ya cobrados no se mueven.
                  </FieldHint>
                </span>
                <span className="registration-action-hint">
                  <button className="btn btn--ghost btn--small cobro-cuenta__remove" type="button" onClick={handleMpDisconnect} disabled={mpActionLoading}>
                    Desconectar
                  </button>
                  <FieldHint label="Qué hace Desconectar">
                    Quita la cuenta y revoca la autorización en Mercado Pago. Los pagos en línea de tus torneos se pausan hasta que conectes otra.
                  </FieldHint>
                </span>
              </div>
            )}
          </div>

          {mpStatus === "not_connected" ? (
            <p className="cobro-muted">Te llevamos a Mercado Pago para autorizar tu cuenta y regresas aquí.</p>
          ) : mpStatus === "disabled" ? (
            <p className="cobro-torneo__warn">La conexión expiró o fue revocada: reconecta tu cuenta para seguir cobrando en línea.</p>
          ) : (
            <p className="cobro-muted">
              <strong className="cobro-strong">Siguiente paso:</strong> en cada torneo elige Mercado Pago en Inscripción y operación.
            </p>
          )}
        </section>
      ) : null}

      <section className="cobro-card" aria-labelledby="cobro-bank-title">
        <div className="cobro-hd">
          <span className="cobro-num">{mpConfigured ? 2 : 1}</span>
          <h2 id="cobro-bank-title">Cuentas bancarias · transferencia</h2>
          <FieldHint label="Cómo funciona el cobro manual">
            Tus datos bancarios se muestran en el link de inscripción; el jugador transfiere y sube su comprobante, y tú marcas el pago en Inscripciones. Sin comisión de Mercado Pago, pero revisas cada pago a mano. Este método requiere autorización del superadmin en torneos con costo.
          </FieldHint>
        </div>
        <p className="cobro-muted">El jugador transfiere y sube su comprobante. La predeterminada se propone en cada torneo nuevo.</p>
        {token ? (
          <BankAccountsSection
            token={token}
            accounts={payload?.bank_accounts ?? []}
            onReload={loadAccount}
            onNotice={setNotice}
            onError={setError}
          />
        ) : null}
      </section>

      {isSuperadmin ? (
        <div className="cobro-divider" aria-hidden="true">
          <span className="cobro-chip cobro-chip--sa">Solo superadmin</span>
        </div>
      ) : null}

      {fee ? (
        <section className={`cobro-card${isSuperadmin ? " cobro-card--sa" : ""}`} aria-labelledby="cobro-fee-title">
          <div className="cobro-hd">
            <span className={`cobro-num${isSuperadmin ? " cobro-num--sa" : ""}`}>{mpConfigured ? 3 : 2}</span>
            <h2 id="cobro-fee-title">Comisión de Torneos123</h2>
          </div>
          <p className="cobro-muted">Cuánto cobra Torneos123 por cada inscripción pagada en línea.</p>

          {isSuperadmin ? (
            <form className="cobro-fee-form" onSubmit={handleSavePlatformSettings}>
              <div className="cobro-fee-grid">
                <div className="cobro-fld">
                  <span className="cobro-lab">Modo de cobro</span>
                  <div>{modeChip(mode)}</div>
                  <span className="cobro-muted">Se define en el servidor; no se cambia aquí. Lo que guardes aplica al modo activo.</span>
                </div>
                <label className="cobro-fld">
                  <span className="cobro-lab">Moneda predeterminada</span>
                  <select
                    className="t123-select"
                    value={platformForm.currency_default}
                    onChange={(e) => setPlatformForm((current) => ({ ...current, currency_default: e.target.value }))}
                  >
                    {platformForm.currency_default !== "MXN" ? <option value={platformForm.currency_default}>{platformForm.currency_default}</option> : null}
                    <option value="MXN">MXN · Peso mexicano</option>
                  </select>
                </label>
                <label className="cobro-fld">
                  <span className="cobro-lab">Tipo de comisión</span>
                  <select
                    className="t123-select"
                    value={platformForm.platform_fee_type}
                    onChange={(e) => setPlatformForm((current) => ({ ...current, platform_fee_type: e.target.value }))}
                  >
                    <option value="fixed">{FEE_TYPE_LABELS.fixed}</option>
                    <option value="percent">{FEE_TYPE_LABELS.percent}</option>
                    <option value="fixed_plus_percent">{FEE_TYPE_LABELS.fixed_plus_percent}</option>
                  </select>
                </label>
                <label className="cobro-fld">
                  <span className="cobro-lab">Monto fijo ({platformForm.currency_default || "MXN"})</span>
                  <input
                    className="cobro-inp"
                    type="number"
                    min="0"
                    step="1"
                    inputMode="decimal"
                    disabled={!formUsesFixed}
                    value={platformForm.platform_fee_fixed_amount}
                    onChange={(e) => setPlatformForm((current) => ({ ...current, platform_fee_fixed_amount: e.target.value }))}
                  />
                  {!formUsesFixed ? <span className="cobro-muted">Se habilita al elegir un tipo con monto fijo.</span> : null}
                </label>
                <label className="cobro-fld">
                  <span className="cobro-lab">Porcentaje (%)</span>
                  <input
                    className="cobro-inp"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    inputMode="decimal"
                    disabled={!formUsesPercent}
                    value={platformForm.platform_fee_percent}
                    onChange={(e) => setPlatformForm((current) => ({ ...current, platform_fee_percent: e.target.value }))}
                  />
                  {!formUsesPercent ? <span className="cobro-muted">Se habilita al elegir un tipo con porcentaje.</span> : null}
                </label>
                <label className="cobro-fld">
                  <span className="cobro-lab">Comisión mínima ({platformForm.currency_default || "MXN"})</span>
                  <input
                    className="cobro-inp"
                    type="number"
                    min="0"
                    step="1"
                    inputMode="decimal"
                    value={platformForm.minimum_platform_fee_amount}
                    onChange={(e) => setPlatformForm((current) => ({ ...current, minimum_platform_fee_amount: e.target.value }))}
                  />
                </label>
                <label className="cobro-switch">
                  <input
                    type="checkbox"
                    role="switch"
                    checked={platformForm.is_active}
                    onChange={(e) => setPlatformForm((current) => ({ ...current, is_active: e.target.checked }))}
                  />
                  Comisión activa
                </label>
              </div>
              <div className="cobro-fee-actions">
                <button className="btn btn--primary cobro-btn-lime" type="submit" disabled={actionLoading}>
                  {actionLoading ? "Guardando…" : "Guardar comisión"}
                </button>
                <Link className="cobro-page__back" to="/superadmin/fees">Comisión que deben los organizadores →</Link>
              </div>
            </form>
          ) : (
            <>
              <dl className="cobro-fee-read">
                <div>
                  <dt>Tipo</dt>
                  <dd>{FEE_TYPE_LABELS[fee.platform_fee_type] ?? fee.platform_fee_type}</dd>
                </div>
                <div>
                  <dt>Monto fijo</dt>
                  <dd>{feeUsesFixed(fee.platform_fee_type) ? `${formatMoney(fee.platform_fee_fixed_amount)} ${fee.currency_default || "MXN"}` : "No aplica"}</dd>
                </div>
                <div>
                  <dt>Porcentaje</dt>
                  <dd>{feeUsesPercent(fee.platform_fee_type) ? `${bpsToPercent(fee.platform_fee_percent_bps)} %` : "No aplica"}</dd>
                </div>
                <div>
                  <dt>Mínimo</dt>
                  <dd>{`${formatMoney(fee.minimum_platform_fee_amount)} ${fee.currency_default || "MXN"}`}</dd>
                </div>
                <div>
                  <dt>Estado</dt>
                  <dd>
                    <span className={`cobro-chip ${fee.is_active ? "cobro-chip--ok" : "cobro-chip--muted"}`}>{fee.is_active ? "Activa" : "Inactiva"}</span>
                  </dd>
                </div>
              </dl>
              <p className="cobro-muted">La define Torneos123.</p>
            </>
          )}
        </section>
      ) : null}
    </div>
  );
}
